import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Select, Empty, StatusBadge, Field, Input, Textarea } from "@/components/kit";
import { STATUS_META } from "@/lib/constants";
import PostEditor from "@/components/PostEditor";
import { Check, X, CalendarClock, Pencil, ImageIcon, Send, Share2, FileVideo, MessageCircle, Copy, Trash2, AlertTriangle } from "lucide-react";

const FILTERS = ["", "PENDING_APPROVAL", "APPROVED", "SCHEDULED", "PUBLISHED", "REJECTED"];
const TABS = [
  { key: "", label: "Todos" },
  { key: "PENDING_APPROVAL", label: "Pendentes" },
  { key: "APPROVED", label: "Aprovados" },
  { key: "SCHEDULED", label: "Programados" },
  { key: "PUBLISHED", label: "Publicados" },
  { key: "REJECTED", label: "Ajustes" },
];
const REAL_CH = ["Instagram", "Facebook"];
const SOON_CH = ["WhatsApp", "Google Business", "YouTube", "YouTube Shorts", "TikTok"];

export default function Aprovacao() {
  const { activeOrgId } = useAuth();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState("");
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [scheduleItem, setScheduleItem] = useState(null);
  const [dateStr, setDateStr] = useState("");
  const [timeStr, setTimeStr] = useState("09:00");
  const [saving, setSaving] = useState(false);
  const [distItem, setDistItem] = useState(null);
  const [distChannels, setDistChannels] = useState([]);
  const [distMode, setDistMode] = useState("now");
  const [distDate, setDistDate] = useState("");
  const [distTime, setDistTime] = useState("09:00");
  const [distSaving, setDistSaving] = useState(false);
  const [waItem, setWaItem] = useState(null);
  const [waText, setWaText] = useState("");
  const [waPhone, setWaPhone] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const openWhatsApp = (p) => {
    const msg = [p.caption, (p.hashtags || []).map((t) => `#${t}`).join(" ")].filter(Boolean).join("\n\n");
    setWaItem(p); setWaText(msg); setWaPhone("");
  };
  const waLink = () => {
    const digits = (waPhone || "").replace(/\D/g, "");
    const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
    return `${base}?text=${encodeURIComponent(waText || "")}`;
  };
  const sendWhatsApp = () => { window.open(waLink(), "_blank", "noopener"); };
  const downloadWaImage = async (mediaId) => {
    try {
      const r = await client.get(`/media/${mediaId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `imagem-${mediaId}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Imagem baixada — anexe no WhatsApp junto com o texto");
    } catch { toast.error("Não foi possível baixar a imagem"); }
  };
  const copyWhatsApp = async () => {
    try { await navigator.clipboard.writeText(waText || ""); toast.success("Mensagem copiada"); }
    catch { toast.error("Não foi possível copiar"); }
  };

  const load = () => {
    if (!activeOrgId) return;
    client.get("/content").then((r) => setItems(r.data));
  };
  useEffect(load, [activeOrgId]);
  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => setBrands(r.data));
  }, [activeOrgId]);

  const act = async (id, status, comment) => {
    try { await client.patch(`/content/${id}`, { status, comment }); toast.success("Atualizado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    const comment = window.prompt("Comentário do ajuste solicitado (opcional):") || "";
    act(id, "REJECTED", comment);
  };
  const removeItem = async (p) => {
    if (!window.confirm(`Excluir "${p.title}" (${p.channel})? Esta ação não pode ser desfeita.`)) return;
    try {
      await client.delete(`/content/${p.id}`);
      toast.success("Conteúdo excluído");
      setItems((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openSchedule = (p) => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    const d = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    setScheduleItem(p);
    setDateStr(p.scheduled_at ? p.scheduled_at.slice(0, 10) : d);
    setTimeStr("09:00");
  };
  const confirmSchedule = async () => {
    if (!scheduleItem) return;
    setSaving(true);
    try {
      const [hh, mm] = timeStr.split(":").map(Number);
      const [y, mo, da] = dateStr.split("-").map(Number);
      const dt = new Date(y, mo - 1, da, Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0);
      const ids = scheduleItem.bulk ? selected : [scheduleItem.id];
      for (const id of ids) await client.patch(`/content/${id}`, { status: "SCHEDULED", scheduled_at: dt.toISOString() });
      toast.success(`${ids.length} post(s) agendado(s) para ${dt.toLocaleDateString("pt-BR")} às ${timeStr}`);
      setScheduleItem(null);
      if (scheduleItem.bulk) setSelected([]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const bulkApprove = async () => {
    try { for (const id of selected) await client.patch(`/content/${id}`, { status: "APPROVED" }); toast.success(`${selected.length} aprovado(s)`); setSelected([]); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openBulkSchedule = () => openSchedule({ bulk: true, title: `${selected.length} posts selecionados` });
  const bulkDelete = async () => {
    if (!window.confirm(`Excluir ${selected.length} post(s)? Esta ação não pode ser desfeita.`)) return;
    try { for (const id of selected) await client.delete(`/content/${id}`); toast.success(`${selected.length} excluído(s)`); setSelected([]); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkPublish = async () => {
    if (!window.confirm(`Publicar agora ${selected.length} post(s)? Os canais conectados vão ao ar de verdade.`)) return;
    setBulkBusy(true);
    let real = 0, sandbox = 0, skipped = 0, fail = 0;
    for (const id of selected) {
      try {
        const { data } = await client.post(`/content/${id}/publish-now`);
        if (data.outcome === "real") real++;
        else if (data.outcome === "sandbox") sandbox++;
        else if (data.outcome === "skipped") skipped++;
        else fail++;
      } catch { fail++; }
    }
    const parts = [`${real} real(is)`, `${sandbox} teste`];
    if (skipped) parts.push(`${skipped} já publicado`);
    parts.push(`${fail} falha(s)`);
    const msg = `Publicação: ${parts.join(", ")}`;
    if (fail) toast.warning(msg); else toast.success(msg);
    setSelected([]); setBulkBusy(false); load();
  };
  const publishNow = async (p) => {
    if (!window.confirm(`Publicar "${p.title}" agora em ${p.channel}? Se a rede estiver conectada, o post irá ao ar de verdade.`)) return;
    try {
      const { data } = await client.post(`/content/${p.id}/publish-now`);
      const msg = data.message || "Publicado";
      if (data.outcome === "real") toast.success(msg);
      else if (data.outcome === "sandbox") toast.warning(msg);
      else toast.error(msg);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openDistribute = (p) => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    const d = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    setDistItem(p);
    setDistChannels(REAL_CH.includes(p.channel) ? [p.channel] : ["Instagram"]);
    setDistMode("now");
    setDistDate(d);
    setDistTime("09:00");
  };
  const toggleDistCh = (ch) =>
    setDistChannels((prev) => prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]);
  const confirmDistribute = async () => {
    if (!distItem || !distChannels.length) return;
    setDistSaving(true);
    try {
      let scheduled_at;
      if (distMode === "schedule") {
        const [hh, mm] = distTime.split(":").map(Number);
        const [y, mo, da] = distDate.split("-").map(Number);
        scheduled_at = new Date(y, mo - 1, da, Number.isFinite(hh) ? hh : 9, Number.isFinite(mm) ? mm : 0, 0, 0).toISOString();
      }
      const { data } = await client.post(`/content/${distItem.id}/distribute`, { channels: distChannels, mode: distMode, scheduled_at });
      toast.success(data.message || "Distribuído");
      setDistItem(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDistSaving(false); }
  };
  const BRAND_COLORS = ["bg-indigo-100 text-indigo-700", "bg-emerald-100 text-emerald-700", "bg-amber-100 text-amber-700", "bg-sky-100 text-sky-700", "bg-rose-100 text-rose-700", "bg-violet-100 text-violet-700", "bg-teal-100 text-teal-700"];
  const brandColor = (id) => {
    const idx = brands.findIndex((b) => b.id === id);
    return BRAND_COLORS[(idx >= 0 ? idx : 0) % BRAND_COLORS.length];
  };

  const scoped = brandFilter ? items.filter((p) => p.brand_id === brandFilter) : items;
  const counts = scoped.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {});
  const visible = (filter ? scoped.filter((p) => p.status === filter) : scoped)
    .slice().sort((a, b) => (a.day || 0) - (b.day || 0) || (a.channel || "").localeCompare(b.channel || ""));

  return (
    <div>
      <PageHeader title="Central de Aprovação" subtitle="Aprove, solicite ajustes e agende. Canais conectados publicam de verdade; os não conectados ficam em modo teste (SANDBOX)."
/>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-slate-600">Marca:</label>
        <select
          data-testid="approval-brand-filter"
          value={brandFilter}
          onChange={(e) => { setBrandFilter(e.target.value); setSelected([]); }}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          <option value="">Todas as marcas</option>
          {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>
        {brandFilter && (
          <span className="text-xs text-slate-400">Mostrando apenas os posts desta marca.</span>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="approval-tabs">
        {TABS.map((t) => {
          const active = filter === t.key;
          const n = t.key ? counts[t.key] || 0 : scoped.length;
          return (
            <button key={t.key || "all"} type="button" data-testid={`tab-${t.key || "all"}`} onClick={() => setFilter(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${active ? "bg-[#3457d5] text-white shadow-sm" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
              {t.label}<span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#3457d5]/20 bg-[#3457d5]/5 px-3 py-2" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-slate-700">{selected.length} selecionado(s)</span>
          <Btn variant="success" className="px-3 py-1.5" data-testid="bulk-approve" onClick={bulkApprove}><Check size={14} /> Aprovar</Btn>
          <Btn variant="secondary" className="px-3 py-1.5" data-testid="bulk-schedule" onClick={openBulkSchedule}><CalendarClock size={14} /> Agendar</Btn>
          <Btn variant="primary" className="px-3 py-1.5" data-testid="bulk-publish" onClick={bulkPublish} disabled={bulkBusy}><Send size={14} /> {bulkBusy ? "Publicando..." : "Publicar agora"}</Btn>
          <Btn variant="ghost" className="px-3 py-1.5 text-rose-500 hover:bg-rose-50" data-testid="bulk-delete" onClick={bulkDelete}><Trash2 size={14} /> Excluir</Btn>
          <button type="button" className="ml-auto text-xs text-slate-500 underline" onClick={() => setSelected([])}>Limpar seleção</button>
        </div>
      )}

      {visible.length ? (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 w-8"><input type="checkbox" data-testid="bulk-select-all"
                  checked={visible.length > 0 && visible.every((v) => selected.includes(v.id))}
                  onChange={(e) => setSelected(e.target.checked ? Array.from(new Set([...selected, ...visible.map((v) => v.id)])) : selected.filter((id) => !visible.some((v) => v.id === id)))} /></th>
                <th className="text-left px-4 py-3">Dia</th>
                <th className="text-left px-4 py-3">Marca</th>
                <th className="text-left px-4 py-3">Conteúdo</th>
                <th className="text-left px-4 py-3">Canal</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`approval-row-${p.id}`}>
                  <td className="px-4 py-3"><input type="checkbox" data-testid={`select-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} /></td>
                  <td className="px-4 py-3 font-semibold text-slate-400">{p.day}</td>
                  <td className="px-4 py-3"><span data-testid={`brand-badge-${p.id}`} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${brandColor(p.brand_id)}`}>{p.brand_name || "—"}</span></td>
                  <td className="px-4 py-3 max-w-md"><div className="flex items-center gap-2"><div className="font-medium text-slate-800 truncate">{p.title}</div>{p.image_media_id && <ImageIcon size={13} className="text-emerald-500 shrink-0" title="Tem arte" />}{p.video_media_id && <FileVideo size={13} className="text-[#3457d5] shrink-0" title="Tem vídeo" />}{p.channel === "Instagram" && !p.image_media_id && <span data-testid={`no-image-warning-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold shrink-0" title="O Instagram exige uma imagem. Gere/anexe uma arte antes de publicar."><AlertTriangle size={11} /> Sem arte</span>}</div><div className="text-xs text-slate-500 truncate">{p.caption}</div></td>
                  <td className="px-4 py-3 text-slate-600">{p.channel}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} />{p.status === "PUBLISHED" && (<span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${p.publish_confirmed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.publish_confirmed ? "Real" : "Teste"}</span>)}{p.publish_failed && p.publish_error && (<div data-testid={`publish-error-${p.id}`} className="mt-1 flex items-start gap-1 rounded-md bg-rose-50 border border-rose-200 px-1.5 py-1 text-[10px] font-medium text-rose-700 max-w-[220px]" title={p.publish_error}><AlertTriangle size={11} className="shrink-0 mt-0.5" /><span className="line-clamp-2">{p.publish_error}</span></div>)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Btn variant="secondary" className="px-2.5 py-1.5" data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} title="Editar post"><Pencil size={15} /></Btn>
                      <Btn variant="success" className="px-2.5 py-1.5" data-testid={`approve-${p.id}`} onClick={() => act(p.id, "APPROVED")} title="Aprovar"><Check size={15} /></Btn>
                      <Btn variant="danger" className="px-2.5 py-1.5" data-testid={`reject-${p.id}`} onClick={() => reject(p.id)} title="Solicitar ajuste"><X size={15} /></Btn>
                      <Btn variant="secondary" className="px-2.5 py-1.5" data-testid={`schedule-${p.id}`} onClick={() => openSchedule(p)} title="Agendar"><CalendarClock size={15} /></Btn>
                      <Btn variant="secondary" className="px-2.5 py-1.5" data-testid={`distribute-${p.id}`} onClick={() => openDistribute(p)} title="Publicar em vários canais"><Share2 size={15} /></Btn>
                      {p.channel === "WhatsApp" ? (
                        <Btn variant="success" className="px-2.5 py-1.5" data-testid={`whatsapp-${p.id}`} onClick={() => openWhatsApp(p)} title="Enviar pelo WhatsApp"><MessageCircle size={15} /></Btn>
                      ) : (
                        <Btn variant="primary" className="px-2.5 py-1.5" data-testid={`publish-now-${p.id}`} onClick={() => publishNow(p)} title="Publicar agora"><Send size={15} /></Btn>
                      )}
                      <Btn variant="ghost" className="px-2.5 py-1.5 text-rose-500 hover:bg-rose-50" data-testid={`delete-${p.id}`} onClick={() => removeItem(p)} title="Excluir conteúdo"><Trash2 size={15} /></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : <Empty>Nenhum conteúdo para este filtro.</Empty>}

      {distItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="distribute-modal" onClick={() => !distSaving && setDistItem(null)}>
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[#3457d5]"><Share2 size={20} /><h3 className="font-display text-lg font-extrabold text-slate-900">Publicar em vários canais</h3></div>
            <p className="mt-1 text-sm text-slate-500 truncate">{distItem.title}</p>

            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Canais (adaptamos o formato de cada um)</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {REAL_CH.map((ch) => (
                  <button key={ch} type="button" data-testid={`dist-ch-${ch}`} onClick={() => toggleDistCh(ch)}
                    className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${distChannels.includes(ch) ? "border-[#3457d5] bg-[#3457d5] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                    {distChannels.includes(ch) ? "✓ " : ""}{ch}
                  </button>
                ))}
                {SOON_CH.map((ch) => (
                  <span key={ch} title="Em breve — conecte e ative esta rede" className="cursor-not-allowed rounded-full border border-dashed border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-300">
                    {ch} · em breve
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Quando</div>
              <div className="mt-2 flex gap-2">
                <button type="button" data-testid="dist-mode-now" onClick={() => setDistMode("now")}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ${distMode === "now" ? "border-[#3457d5] bg-[#3457d5]/5 text-[#3457d5]" : "border-slate-200 text-slate-600"}`}>Publicar agora</button>
                <button type="button" data-testid="dist-mode-schedule" onClick={() => setDistMode("schedule")}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ${distMode === "schedule" ? "border-[#3457d5] bg-[#3457d5]/5 text-[#3457d5]" : "border-slate-200 text-slate-600"}`}>Agendar</button>
              </div>
              {distMode === "schedule" && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Field label="Data"><Input type="date" value={distDate} data-testid="dist-date" onChange={(e) => setDistDate(e.target.value)} /></Field>
                  <Field label="Horário"><Input type="time" value={distTime} data-testid="dist-time" onChange={(e) => setDistTime(e.target.value)} /></Field>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="ghost" data-testid="dist-cancel" disabled={distSaving} onClick={() => setDistItem(null)}>Cancelar</Btn>
              <Btn data-testid="dist-confirm" disabled={distSaving || !distChannels.length} onClick={confirmDistribute}>
                {distSaving ? "Enviando..." : (distMode === "now" ? `Publicar em ${distChannels.length} canal(is)` : `Agendar em ${distChannels.length} canal(is)`)}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {scheduleItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="approval-schedule-modal" onClick={() => !saving && setScheduleItem(null)}>
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[#3457d5]"><CalendarClock size={20} /><h3 className="font-display text-lg font-extrabold text-slate-900">Agendar publicação</h3></div>
            <p className="mt-1 text-sm text-slate-500 truncate">{scheduleItem.title} · {scheduleItem.channel}</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label="Data"><Input type="date" value={dateStr} data-testid="approval-date-input" onChange={(e) => setDateStr(e.target.value)} /></Field>
              <Field label="Horário"><Input type="time" value={timeStr} data-testid="approval-time-input" onChange={(e) => setTimeStr(e.target.value)} /></Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="ghost" data-testid="approval-schedule-cancel" disabled={saving} onClick={() => setScheduleItem(null)}>Cancelar</Btn>
              <Btn data-testid="approval-schedule-confirm" disabled={saving} onClick={confirmSchedule}>{saving ? "Agendando..." : "Confirmar agendamento"}</Btn>
            </div>
          </div>
        </div>
      )}

      {waItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="whatsapp-modal" onClick={() => setWaItem(null)}>
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-emerald-600"><MessageCircle size={20} /><h3 className="font-display text-lg font-extrabold text-slate-900">Enviar pelo WhatsApp</h3></div>
            <p className="mt-1 text-sm text-slate-500 truncate">{waItem.title}</p>
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-2.5 text-xs text-emerald-700">
              Modo manual: revise a mensagem e toque em <b>Abrir WhatsApp</b> para enviar pelo seu número comercial, ou <b>Copie</b> e cole na sua lista de transmissão.
              {waItem.image_media_id && (<><br />O WhatsApp não permite anexar imagem pelo link. Toque em <b>Baixar imagem</b> e anexe manualmente junto com o texto.</>)}
            </div>
            <div className="mt-4">
              <Field label="Mensagem"><Textarea data-testid="whatsapp-text" rows={7} value={waText} onChange={(e) => setWaText(e.target.value)} /></Field>
            </div>
            <div className="mt-3">
              <Field label="Telefone do destinatário (opcional, com DDI/DDD)"><Input data-testid="whatsapp-phone" value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="Ex.: 5511999990000" /></Field>
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Btn variant="ghost" data-testid="whatsapp-cancel" onClick={() => setWaItem(null)}>Fechar</Btn>
              {waItem.image_media_id && (
                <Btn variant="secondary" data-testid="whatsapp-download-image" onClick={() => downloadWaImage(waItem.image_media_id)}><ImageIcon size={15} /> Baixar imagem</Btn>
              )}
              <Btn variant="secondary" data-testid="whatsapp-copy" onClick={copyWhatsApp}><Copy size={15} /> Copiar</Btn>
              <Btn variant="success" data-testid="whatsapp-send" onClick={sendWhatsApp}><MessageCircle size={15} /> Abrir WhatsApp</Btn>
            </div>
          </div>
        </div>
      )}

      {editing && <PostEditor item={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
